import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronLeft,
  Headset,
  Loader2,
  Plus,
  Search,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../config';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import TicketStatusBadge from '../components/support/TicketStatusBadge';
import TicketDetailPanel from '../components/support/TicketDetailPanel';
import SupportMessageEditor from '../components/support/SupportMessageEditor';
import {
  TICKET_CATEGORIES,
  TICKET_PRIORITIES,
  formatTicketDate,
  stringifySupportBody,
  isSupportBodyEmpty,
} from '../lib/supportConstants';

function TicketListRow({ row, selected, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(row.id)}
      className={`w-full text-left px-3 py-3 transition-colors border-b border-slate-100 dark:border-slate-800 hover:bg-slate-100/80 dark:hover:bg-slate-800/60 ${
        selected ? 'bg-blue-50 dark:bg-blue-950/40' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <span className={`text-sm truncate ${selected ? 'font-semibold' : 'font-medium'} text-slate-900 dark:text-white`}>
          {row.subject}
        </span>
        <span className="shrink-0 text-[10px] font-mono text-slate-400">#{row.ticket_number}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-1.5">
        <TicketStatusBadge status={row.status} />
        <span className="text-[11px] text-slate-400">{formatTicketDate(row.last_message_at || row.updated_at)}</span>
      </div>
    </button>
  );
}

export default function HelpCenterPage() {
  const { currentOrg } = useAuth();
  const orgId = currentOrg?.id;
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [mobileShowDetail, setMobileShowDetail] = useState(false);
  const [newSubject, setNewSubject] = useState('');
  const [newCategory, setNewCategory] = useState('feedback');
  const [newPriority, setNewPriority] = useState('normal');
  const createEditorRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  const loadTickets = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/organizations/${orgId}/support/tickets`, {
        params: { q: debouncedQ || undefined },
      });
      setTickets(res.data || []);
    } catch {
      toast.error('Failed to load tickets');
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, [orgId, debouncedQ]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const loadDetail = useCallback(async (ticketId, { silent = false } = {}) => {
    if (!orgId || !ticketId) {
      setDetail(null);
      return;
    }
    if (silent) setRefreshing(true);
    else setDetailLoading(true);
    try {
      const res = await axios.get(`${API_URL}/organizations/${orgId}/support/tickets/${ticketId}`);
      setDetail(res.data);
    } catch {
      toast.error('Failed to load ticket');
      if (!silent) setDetail(null);
    } finally {
      if (silent) setRefreshing(false);
      else setDetailLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
    else setDetail(null);
  }, [selectedId, loadDetail]);

  const selectTicket = (id) => {
    setSelectedId(id);
    setMobileShowDetail(true);
  };

  const handleCreate = async () => {
    if (!orgId || !newSubject.trim()) return;
    const data = await createEditorRef.current?.save();
    if (isSupportBodyEmpty(data)) {
      toast.error('Please enter a message');
      return;
    }
    const body = stringifySupportBody(data);
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/organizations/${orgId}/support/tickets`, {
        subject: newSubject.trim(),
        category: newCategory,
        priority: newPriority,
        body,
      });
      toast.success('Ticket created');
      setCreateOpen(false);
      setNewSubject('');
      await createEditorRef.current?.clear();
      await loadTickets();
      setSelectedId(res.data.id);
      setDetail(res.data);
      setMobileShowDetail(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRefresh = async () => {
    if (!selectedId) return;
    await loadDetail(selectedId, { silent: true });
    await loadTickets();
  };

  const handleReply = async (body) => {
    if (!orgId || !selectedId) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/organizations/${orgId}/support/tickets/${selectedId}/messages`, { body });
      await loadDetail(selectedId);
      await loadTickets();
      toast.success('Reply sent');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reply');
    } finally {
      setSubmitting(false);
    }
  };

  if (!orgId) {
    return (
      <div className="p-8 max-w-5xl mx-auto text-center text-slate-500">
        Select a workspace to use the help center.
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:px-8 max-w-none flex flex-col h-[calc(100dvh-4rem)] min-h-0">
      <div className="shrink-0 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl font-bold font-[Manrope] text-slate-900 dark:text-white flex items-center gap-2">
            <Headset className="w-6 h-6 text-blue-600" />
            Help Center
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-0.5 text-sm">
            Submit feedback, report bugs, or request enhancements.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="shrink-0" size="sm">
          <Plus className="w-4 h-4 mr-2" />
          New ticket
        </Button>
      </div>

      <div className="flex flex-1 min-h-0 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden bg-white dark:bg-slate-950 shadow-sm">
        <aside
          className={`flex flex-col w-full md:w-[min(360px,38%)] lg:w-[340px] shrink-0 border-r border-slate-200 dark:border-slate-800 min-h-0 ${
            mobileShowDetail ? 'hidden md:flex' : 'flex'
          }`}
        >
          <div className="shrink-0 p-3 border-b border-slate-200 dark:border-slate-800">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                className="pl-9 h-9 bg-slate-50 dark:bg-slate-800/50"
                placeholder="Search tickets…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center gap-2 text-slate-500 py-16">
                <Loader2 className="w-4 h-4 animate-spin" />
              </div>
            ) : tickets.length === 0 ? (
              <p className="text-sm text-slate-500 py-16 px-4 text-center">No tickets yet. Create one to get started.</p>
            ) : (
              tickets.map((row) => (
                <TicketListRow key={row.id} row={row} selected={selectedId === row.id} onSelect={selectTicket} />
              ))
            )}
          </div>
        </aside>

        <main
          className={`flex-1 min-w-0 flex flex-col min-h-0 ${
            !mobileShowDetail && !selectedId ? 'hidden md:flex' : 'flex'
          }`}
        >
          {mobileShowDetail && selectedId ? (
            <div className="md:hidden shrink-0 flex items-center gap-2 px-3 py-2 border-b border-slate-200 dark:border-slate-800">
              <Button variant="ghost" size="sm" className="h-8 -ml-2" onClick={() => setMobileShowDetail(false)}>
                <ChevronLeft className="w-4 h-4 mr-1" />
                Tickets
              </Button>
            </div>
          ) : null}
          <TicketDetailPanel
            ticket={detail}
            mode="client"
            orgId={orgId}
            loading={detailLoading && !!selectedId}
            refreshing={refreshing}
            submitting={submitting}
            onRefresh={selectedId ? handleRefresh : undefined}
            onReply={handleReply}
          />
        </main>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent
          className="max-w-2xl max-h-[90vh] overflow-y-auto"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={(e) => {
            if (e.target.closest?.('.ql-toolbar') || e.target.closest?.('.ql-editor') || e.target.closest?.('.support-quill-editor')) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>New support ticket</DialogTitle>
            <DialogDescription>
              Share feedback, report a bug, or suggest an enhancement. Attach screenshots or files in the message.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="ticket-subject">Subject</Label>
              <Input
                id="ticket-subject"
                value={newSubject}
                onChange={(e) => setNewSubject(e.target.value)}
                placeholder="Brief summary of your request"
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="ticket-category">Category</Label>
                <select
                  id="ticket-category"
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                >
                  {TICKET_CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ticket-priority">Priority</Label>
                <select
                  id="ticket-priority"
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                  value={newPriority}
                  onChange={(e) => setNewPriority(e.target.value)}
                >
                  {TICKET_PRIORITIES.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Message</Label>
              {createOpen ? (
                <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
                  <SupportMessageEditor
                    key="new-ticket-editor"
                    ref={createEditorRef}
                    orgId={orgId}
                    placeholder="Describe your request in detail…"
                    expanded
                  />
                </div>
              ) : null}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={submitting || !newSubject.trim()}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Submit ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
